import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { Subscription } from "rxjs";

import {
  BscArea,
  BscCatalogos,
  BscCiclo,
  BscComparativo,
  BscComparativoArea,
  BscComparativoFila,
  BscRes,
  EstadoComparativoBsc,
  EstatusPlaneacionBsc,
  OrigenPlaneacionBsc,
  SemaforoBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";
import { ESTADO_COMPARATIVO, ESTATUS_PLANEACION, EstadoBsc } from "../bsc-estados";
import { SIN_DATO, fechaCorta, meta, numero, pct, pts } from "../bsc-formato";
import { AcumuladoQBscComponent } from "../graficas/acumulado-q-bsc.component";
import { TileBsc, TilesBscComponent } from "../tiles/tiles-bsc.component";

/**
 * Qué se puede enseñar de la comparación:
 *   DATOS          tiles, gráfica, los dos años (o las áreas) y la tabla KPI por KPI;
 *   SIN_LECTURA    un año no se pudo leer: no se afirma que falte nada, solo los avisos;
 *   SIN_ANIO       un año no está dado de alta en el BSC;
 *   SIN_PLANEACION el área (o la empresa) no tiene planeación en uno de los dos años.
 */
type EstadoComparativo = "DATOS" | "SIN_LECTURA" | "SIN_ANIO" | "SIN_PLANEACION";

/** Mismo corte (los dos años al mismo trimestre) o cierre anual (cada uno a su último autorizado). */
type ModoComparativo = "corte" | "cierre";

/** El filtro de estado de la tabla. */
type FiltroEstado = "TODOS" | EstadoComparativoBsc;

/** Una opción de un combo de año. */
interface OpcionAnio {
  anio: number;
  texto: string;
}

/** Una celda de alcance: el porcentaje, "Inicia en Qn", "Retirado" o "—", y su nota. */
interface CeldaAlcance {
  texto: string;
  clase: "" | "is-vacio";
  nota: string;
}

/** Un renglón de la tabla, armado UNA vez por respuesta. */
interface FilaVista {
  clave: string;
  idBscArea: number | null;
  area: string;
  objetivo: string;
  indicador: string;
  busqueda: string;
  metaA: string;
  metaB: string;
  alcanceA: CeldaAlcance;
  alcanceB: CeldaAlcance;
  delta: string;
  deltaClase: "" | "is-arriba" | "is-abajo";
  estado: EstadoBsc<EstadoComparativoBsc> | null;
  estadoTexto: string;
  valorEstado: EstadoComparativoBsc | null;
  pesosCambiados: boolean;
  cambioMeta: string;
  cambioPesos: string;
  motivo: string;
  quien: string;
  sinCambio: string;
  idEjercicioKpiA: number | null;
  idEjercicioKpiB: number | null;
  fila: BscComparativoFila;
}

/**
 * Los renglones visibles de un área: un <tbody> por grupo. En toda la empresa
 * lo encabeza el nombre del área (<th scope="rowgroup">), que así se anuncia
 * en sus KPI; con un área es un solo grupo sin encabezado.
 */
interface GrupoVista {
  clave: string;
  area: string;
  conEncabezado: boolean;
  filas: FilaVista[];
}

/** Un renglón de la tarjeta de áreas (toda la empresa). */
interface FilaArea {
  idBscArea: number | null;
  area: string;
  alcanceA: string;
  alcanceB: string;
  delta: string;
  deltaClase: "" | "is-arriba" | "is-abajo";
  nota: string;
}

/** Un año en la tarjeta "Los dos años". */
interface FichaAnio {
  anio: number;
  rol: string;
  planeacion: string;
  origen: string;
  trimestre: string;
  objetivo: string;
  porOmision: boolean;
  datos: string;
}

/** Lo que se lee de un año (A o B) para su tarjeta en "Los dos años". */
interface DatosAnio {
  anio: number | undefined;
  rol: string;
  estatus: EstatusPlaneacionBsc | undefined;
  origen: OrigenPlaneacionBsc | undefined;
  q: number | undefined;
  objetivo: number | undefined;
  porOmision: boolean | undefined;
  datosHasta: number | undefined;
  mismoCorte: boolean;
}

const TRIMESTRES = [1, 2, 3, 4];
const MODOS = new Set<ModoComparativo>(["corte", "cierre"]);

/** Rutas de las otras pantallas del módulo. */
const RUTA = {
  tablero: "/admin/bsc/tablero",
  area: "/admin/bsc/tablero/area",
  kpi: "/admin/bsc/tablero/kpi",
  cargaExcel: "/admin/bsc/carga-excel",
  planeacion: "/admin/bsc/planeacion",
  parametros: "/admin/bsc/parametros",
};

/**
 * BSC · T2 Comparativo anual (docs/BSC.md §10 y Anexo B, maqueta
 * «T2-ComparativoAnual»): dos años de un área, o de toda la empresa, KPI por
 * KPI emparejados por el KPI maestro. Tiles (alcance de los dos al mismo
 * corte con el delta, cierre anual del año de referencia, KPIs conservados /
 * nuevos / retirados / con meta modificada y el objetivo Solser de cada
 * año), el acumulado por trimestre de los dos años, los dos años (o las
 * áreas) y la tabla con metas, alcances, delta, cambio de meta con su motivo
 * y el estado, con la ficha (A3) de cada año.
 *
 * Solo lectura (comparativo.do, BscComparativoBuss): la hoja
 * admin/bsc/comparativo. «Crear <B+1> a partir de <B>» abre la Planeación
 * (T3) en ese año y área, donde está «Crear a partir de <B>» con sus
 * validaciones; sale con puedePlanear del área y la planeación de B
 * autorizada. «Exportar tabla» baja lo que se ve con xlsx en el navegador.
 *
 * Base visual: la de A1/A2 (hero, barra y tabla de table-page, tiles de
 * app-tiles-bsc y tarjetas .tickets-card dentro de un .tickets-dashboard).
 * La selección viaja en la URL (?anioA=&anioB=&idBscArea=&modo=&q=) para
 * volver al mismo comparativo desde la ficha.
 */
@Component({
  selector: "app-comparativo-anual-bsc",
  standalone: true,
  templateUrl: "./comparativo-anual-bsc.component.html",
  styleUrls: [
    "../../dashboard/shared/dashboard-tarjetas.css",
    "../bsc.shared.css",
    "./comparativo-anual-bsc.component.css",
  ],
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    TilesBscComponent,
    AcumuladoQBscComponent,
  ],
})
export class ComparativoAnualBscComponent implements OnInit, OnDestroy {
  readonly ruta = RUTA;
  readonly trimestres = TRIMESTRES;
  readonly sinDato = SIN_DATO;
  readonly filtrosEstado: FiltroEstado[] = ["TODOS", "CONSERVADO", "META_MODIFICADA", "NUEVO", "RETIRADO"];

  /* ---------- Selección ---------- */
  anios: OpcionAnio[] = [];
  areas: BscArea[] = [];
  anioA: number | null = null;
  anioB: number | null = null;
  /** Nulo = toda la empresa. */
  idBscArea: number | null = null;
  modo: ModoComparativo = "corte";
  /** Con mismo corte: nulo = el corte vigente de B (lo decide el servidor). */
  trimestre: number | null = null;
  anioEnCurso: number = new Date().getFullYear();
  cargandoCatalogos = true;
  errorCatalogos = "";

  /* ---------- Respuesta ---------- */
  loading = false;
  error = "";
  comparativo: BscComparativo | null = null;
  estado: EstadoComparativo = "DATOS";
  avisos: string[] = [];
  tiles: TileBsc[] = [];
  filas: FilaVista[] = [];
  filasVisibles: FilaVista[] = [];
  gruposVisibles: GrupoVista[] = [];
  filtro = "";
  filtroEstado: FiltroEstado = "TODOS";
  filasArea: FilaArea[] = [];
  fichasAnio: FichaAnio[] = [];
  /** "Q1 · 20% / 25%" por trimestre, debajo de la gráfica. */
  resumenQ: { q: number; texto: string }[] = [];
  /** El año que falta o no se pudo leer (estados vacíos). */
  anioFaltante: number | null = null;

  puedePlanear = false;
  puedeCargarExcel = false;
  puedeConfigurar = false;
  exportando = false;
  errorExportar = "";

  private consulta?: Subscription;
  private catalogo?: Subscription;

  constructor(
    private readonly bscService: BscService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.anioA = anioValido(params.get("anioA"));
    this.anioB = anioValido(params.get("anioB"));
    this.idBscArea = idValido(params.get("idBscArea"));
    const modo = params.get("modo") as ModoComparativo;
    this.modo = MODOS.has(modo) ? modo : "corte";
    const q = Number(params.get("q"));
    this.trimestre = Number.isInteger(q) && q >= 1 && q <= 4 ? q : null;
    this.cargarCatalogos();
  }

  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.catalogo?.unsubscribe();
  }

  /* ============================ Catálogos y selección ============================ */

  private cargarCatalogos(): void {
    this.cargandoCatalogos = true;
    this.catalogo = this.bscService.catalogos().subscribe({
      next: (res) => {
        this.cargandoCatalogos = false;
        if (res?.codigoError) {
          this.errorCatalogos = res.codigoError === "AUTH-001"
            ? "Tu sesión caducó. Vuelve a entrar."
            : res.mensajeError || "No se pudieron consultar los años y las áreas del BSC.";
        }
        this.armaCatalogos(res?.catalogos ?? {});
        this.consultar();
      },
      error: () => {
        this.cargandoCatalogos = false;
        this.errorCatalogos = "No se pudieron consultar los años y las áreas del BSC: sin conexión con el servidor.";
        this.armaCatalogos({});
        this.consultar();
      },
    });
  }

  /** Los años (los ciclos más el en curso y los de la URL) y las áreas activas; completa la selección. */
  private armaCatalogos(catalogos: BscCatalogos): void {
    this.anioEnCurso = catalogos.anioEnCurso ?? new Date().getFullYear();
    const porAnio = new Map<number, BscCiclo>();
    (catalogos.anios ?? []).forEach((c) => {
      if (c.anio != null) {
        porAnio.set(c.anio, c);
      }
    });
    this.anioB ??= this.anioEnCurso;
    if (this.anioA == null || this.anioA >= this.anioB) {
      this.anioA = this.anioB - 1;
    }
    const anios = new Set<number>(porAnio.keys());
    [this.anioEnCurso, this.anioA, this.anioB].forEach((a) => anios.add(a));
    this.anios = Array.from(anios)
      .sort((a, b) => b - a)
      .map((anio) => ({ anio, texto: textoAnio(anio, porAnio.get(anio)) }));
    this.areas = catalogos.areas ?? [];
    // Un área de la URL que ya no está activa (o de otra empresa) la valida el servidor; su nombre llega con la respuesta.
    if (this.idBscArea != null) {
      this.nombraArea(this.idBscArea, null);
    }
  }

  /**
   * El combo trae solo las áreas activas (catalogos.do) y la tabla de toda la empresa también las inactivas con
   * planeación: el área que falta entra con su nombre (o «Área N» mientras no se sepa) y ese provisional se cambia
   * por el nombre que contesta el servidor. Sin ella, el combo quedaba en blanco y no la volvía a ofrecer.
   */
  private nombraArea(idBscArea: number, nombre: string | null | undefined): void {
    const provisional = `Área ${idBscArea}`;
    const actual = this.areas.find((a) => a.idBscArea === idBscArea);
    if (!actual) {
      this.areas = [...this.areas, { idBscArea, nombre: nombre || provisional }];
    } else if (nombre && actual.nombre === provisional && nombre !== provisional) {
      this.areas = this.areas.map((a) => (a === actual ? { ...a, nombre } : a));
    }
  }

  /** Las opciones de A: los años anteriores a B. */
  get aniosA(): OpcionAnio[] {
    return this.anioB == null ? this.anios : this.anios.filter((a) => a.anio < (this.anioB as number));
  }

  /** Las opciones de B: los años posteriores al más antiguo (A siempre tiene uno antes). */
  get aniosB(): OpcionAnio[] {
    const minimo = Math.min(...this.anios.map((a) => a.anio));
    return this.anios.filter((a) => a.anio > minimo);
  }

  onAnioA(): void {
    this.cambio();
  }

  onAnioB(): void {
    // A siempre antes que B: si B lo alcanza, A pasa al año anterior a B.
    if (this.anioA != null && this.anioB != null && this.anioA >= this.anioB) {
      this.anioA = this.anioB - 1;
      if (!this.anios.some((a) => a.anio === this.anioA)) {
        this.anios = [...this.anios, { anio: this.anioA, texto: `${this.anioA}` }].sort((a, b) => b.anio - a.anio);
      }
    }
    this.cambio();
  }

  onArea(): void {
    this.filtro = "";
    this.filtroEstado = "TODOS";
    this.cambio();
  }

  onModo(): void {
    this.cambio();
  }

  onTrimestre(): void {
    this.cambio();
  }

  /** Desde la tarjeta de áreas: el comparativo de esa área (una inactiva entra al combo con su nombre). */
  verArea(a: FilaArea): void {
    if (a.idBscArea == null) {
      return;
    }
    this.nombraArea(a.idBscArea, a.area === SIN_DATO ? null : a.area);
    this.idBscArea = a.idBscArea;
    this.onArea();
  }

  private cambio(): void {
    this.errorExportar = "";
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        anioA: this.anioA,
        anioB: this.anioB,
        idBscArea: this.idBscArea,
        modo: this.modo,
        q: this.modo === "corte" ? this.trimestre : null,
      },
      replaceUrl: true,
    });
    this.consultar();
  }

  /* ============================ Consulta ============================ */

  consultar(): void {
    if (this.anioA == null || this.anioB == null) {
      return;
    }
    const pedido = this.clavePedido();
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    this.cdr.detectChanges();
    this.consulta = this.bscService
      .comparativo(this.anioA, this.anioB, this.idBscArea, this.modo === "corte", this.trimestre)
      .subscribe({
        next: (res) => {
          if (pedido !== this.clavePedido()) {
            return;
          }
          this.loading = false;
          if (res?.codigoError) {
            this.error = res.codigoError === "AUTH-001"
              ? "Tu sesión caducó. Vuelve a entrar."
              : res.mensajeError || "No se pudo consultar el comparativo.";
            this.comparativo = null;
            this.cdr.detectChanges();
            return;
          }
          this.aplica(res);
          this.cdr.detectChanges();
        },
        error: () => {
          if (pedido !== this.clavePedido()) {
            return;
          }
          this.loading = false;
          this.error = "No se pudo consultar el comparativo: sin conexión con el servidor.";
          this.comparativo = null;
          this.cdr.detectChanges();
        },
      });
  }

  private clavePedido(): string {
    return `${this.anioA}|${this.anioB}|${this.idBscArea}|${this.modo}|${this.trimestre}`;
  }

  private aplica(res: BscRes): void {
    const c: BscComparativo = res.comparativo ?? {};
    this.comparativo = c;
    if (c.idBscArea != null && c.area) {
      this.nombraArea(c.idBscArea, c.area);
    }
    this.puedePlanear = res.puedePlanear === true;
    this.puedeCargarExcel = res.puedeCargarExcel === true;
    this.puedeConfigurar = res.puedeConfigurar === true;
    this.avisos = c.avisos ?? [];
    this.estado = this.estadoDe(c);
    this.tiles = this.estado === "DATOS" ? this.armaTiles(c) : [];
    this.filas = this.estado === "DATOS" ? this.armaFilas(c) : [];
    this.aplicaFiltro();
    this.filasArea = this.estado === "DATOS" && c.idBscArea == null ? (c.areas ?? []).map((a) => filaArea(a)) : [];
    this.fichasAnio = this.estado === "DATOS" && c.idBscArea != null ? this.armaFichas(c) : [];
    this.resumenQ = TRIMESTRES.map((q) => ({
      q,
      texto: `${pct(c.serieA?.[q - 1])} / ${pct(c.serieB?.[q - 1])}`,
    }));
  }

  private estadoDe(c: BscComparativo): EstadoComparativo {
    const a = c.anioA ?? null;
    const b = c.anioB ?? null;
    if (c.leidoA === false || c.leidoB === false) {
      this.anioFaltante = c.leidoA === false ? a : b;
      return "SIN_LECTURA";
    }
    if (c.estatusCicloA == null || c.estatusCicloB == null) {
      this.anioFaltante = c.estatusCicloA == null ? a : b;
      return "SIN_ANIO";
    }
    const sinA = c.idBscArea != null ? c.estatusPlaneacionA == null : !c.planeacionesA;
    const sinB = c.idBscArea != null ? c.estatusPlaneacionB == null : !c.planeacionesB;
    if (sinA || sinB) {
      this.anioFaltante = sinA ? a : b;
      return "SIN_PLANEACION";
    }
    this.anioFaltante = null;
    return "DATOS";
  }

  /* ============================ Encabezado ============================ */

  get nombreArea(): string {
    if (this.idBscArea == null) {
      return "Toda la empresa";
    }
    return this.comparativo?.area || this.areas.find((a) => a.idBscArea === this.idBscArea)?.nombre || "Área";
  }

  get titulo(): string {
    if (this.anioA == null || this.anioB == null) {
      return "Comparativo anual";
    }
    return `Comparativo ${this.anioA} vs ${this.anioB} · ${this.nombreArea}`;
  }

  /** "Mismo corte · Q2" o "Cierre anual · 2025 Q4 / 2026 Q1". */
  get textoCorte(): string {
    const c = this.comparativo;
    if (this.modo === "cierre") {
      if (!c || this.loading) {
        return "Cierre anual";
      }
      const qa = c.trimestreA ? `Q${c.trimestreA}` : SIN_DATO;
      const qb = c.trimestreB ? `Q${c.trimestreB}` : SIN_DATO;
      return `Cierre anual · ${c.anioA} ${qa} / ${c.anioB} ${qb}`;
    }
    const q = c && !this.loading ? c.trimestre : this.trimestre;
    return q ? `Mismo corte · Q${q}` : "Mismo corte";
  }

  /** La etiqueta de la opción "trimestre vigente" del combo. */
  get textoVigente(): string {
    return this.anioB != null ? `El vigente de ${this.anioB}` : "El vigente";
  }

  /** «Crear <B+1> a partir de <B>»: área, puedePlanear, B autorizada y B+1 dentro de lo que T3 abre. */
  get puedeCrearSiguiente(): boolean {
    const c = this.comparativo;
    return (
      !this.loading &&
      this.puedePlanear &&
      c?.idBscArea != null &&
      c.anioB != null &&
      c.estatusPlaneacionB === "AUTORIZADA" &&
      c.anioB + 1 <= this.anioEnCurso + 1
    );
  }

  get queryPlaneacion(): Record<string, number | null> {
    return { anio: (this.comparativo?.anioB ?? 0) + 1, idBscArea: this.comparativo?.idBscArea ?? null };
  }

  get queryTablero(): Record<string, number | null> {
    return { anio: this.anioB };
  }

  /** Nota del pie de la tabla sobre los motivos. */
  get hayImportados(): boolean {
    const c = this.comparativo;
    return [c?.origenPlaneacionA, c?.origenPlaneacionB, c?.origenCicloA, c?.origenCicloB].some(
      (o) => o === "IMPORTACION_HISTORICA" || o === "IMPORTACION_ARRANQUE",
    );
  }

  /* ============================ Tiles ============================ */

  private armaTiles(c: BscComparativo): TileBsc[] {
    return [this.tileAlcance(c), this.tileCierre(c), this.tileKpis(c), this.tileObjetivo(c)];
  }

  private tileAlcance(c: BscComparativo): TileBsc {
    const a = numero(c.alcanceA);
    const b = numero(c.alcanceB);
    const d = numero(c.delta);
    const tile: TileBsc = {
      etiqueta: c.mismoCorte ? `Alcance al corte Q${c.trimestre ?? ""}`.trim() : "Alcance al cierre",
      valor: pct(b),
      meta: metaAlcance(c, a),
      icono: "compare_arrows",
      clase: claseAlcance(d),
    };
    if (d != null) {
      tile.nota = `${pts(d)} ${sentidoDelta(d)} ${c.anioA}` +
        (c.vivoB ? " · en vivo" : "");
      tile.notaClase = notaClaseDelta(d);
    } else if (b == null) {
      tile.nota = `${c.anioB} sin alcance que comparar`;
    }
    return tile;
  }

  private tileCierre(c: BscComparativo): TileBsc {
    const cierre = numero(c.cierreAnualA);
    return {
      etiqueta: `Cierre anual ${c.anioA}`,
      valor: pct(cierre),
      meta: cierre != null
        ? `de ${pct(c.programadoCierreA)} programado`
        : `${c.anioA} no tiene su Q4 autorizado`,
      icono: "flag",
      clase: cierre != null ? "ticket-kpi--active" : "ticket-kpi--muted",
      nota: cierre != null ? `Referencia para ${c.anioB}` : undefined,
    };
  }

  private tileKpis(c: BscComparativo): TileBsc {
    const partes = [
      `${c.kpisNuevos ?? 0} ${c.kpisNuevos === 1 ? "nuevo" : "nuevos"}`,
      `${c.kpisRetirados ?? 0} ${c.kpisRetirados === 1 ? "retirado" : "retirados"}`,
      `${c.kpisMetaModificada ?? 0} con meta modificada`,
    ];
    if ((c.kpisPesosModificados ?? 0) > 0) {
      partes.push(`${c.kpisPesosModificados} con pesos distintos`);
    }
    return {
      etiqueta: "KPIs",
      valor: `${c.kpisConservados ?? 0}`,
      meta: c.kpisConservados === 1 ? "conservado" : "conservados",
      icono: "fact_check",
      clase: "ticket-kpi--active",
      nota: partes.join(" · "),
    };
  }

  private tileObjetivo(c: BscComparativo): TileBsc {
    const omision: string[] = [];
    if (c.objetivoPorOmisionA) {
      omision.push(`${c.anioA}`);
    }
    if (c.objetivoPorOmisionB) {
      omision.push(`${c.anioB}`);
    }
    return {
      etiqueta: "Objetivo Solser",
      valor: pct(c.objetivoB),
      meta: `${c.anioB} · ${pct(c.objetivoA)} en ${c.anioA}`,
      icono: "track_changes",
      clase: "ticket-kpi--active",
      nota: omision.length > 0
        ? `Umbral por defecto en ${omision.join(" y ")}: nunca se capturó`
        : "El umbral gerencial de cada año",
      notaClase: omision.length > 0 ? "is-aviso" : undefined,
    };
  }

  /* ============================ Los dos años y las áreas ============================ */

  private armaFichas(c: BscComparativo): FichaAnio[] {
    return [
      ficha({
        anio: c.anioA, rol: "Año A · referencia", estatus: c.estatusPlaneacionA, origen: c.origenPlaneacionA, q: c.trimestreA,
        objetivo: c.objetivoA, porOmision: c.objetivoPorOmisionA, datosHasta: c.datosHastaA, mismoCorte: c.mismoCorte === true,
      }),
      ficha({
        anio: c.anioB, rol: "Año B · comparado", estatus: c.estatusPlaneacionB, origen: c.origenPlaneacionB, q: c.trimestreB,
        objetivo: c.objetivoB, porOmision: c.objetivoPorOmisionB, datosHasta: c.datosHastaB, mismoCorte: c.mismoCorte === true,
      }),
    ];
  }

  /* ============================ Tabla ============================ */

  private armaFilas(c: BscComparativo): FilaVista[] {
    return (c.filas ?? []).map((f) => this.fila(c, f));
  }

  private fila(c: BscComparativo, f: BscComparativoFila): FilaVista {
    const objetivo = f.objetivo || `KPI ${f.idKpi ?? ""}`.trim();
    const estado = ESTADO_COMPARATIVO.find((e) => e.valor === f.estado) ?? null;
    const d = numero(f.delta);
    const metaCambiada = f.estado === "META_MODIFICADA";
    const conCambio = metaCambiada || f.pesosCambiados === true;
    let motivo = "";
    if (conCambio) {
      motivo = f.motivo ? `Motivo: ${f.motivo}` : this.sinMotivo(c);
    }
    const quien = conCambio && f.motivo && (f.usuarioCambio || f.fechaCambio) ? quienCambio(f) : "";
    return {
      clave: `${f.idBscArea ?? ""}|${f.idKpi ?? objetivo}`,
      idBscArea: f.idBscArea ?? null,
      area: f.area || "",
      objetivo,
      indicador: f.indicador || "",
      busqueda: normaliza(`${objetivo} ${f.indicador ?? ""} ${f.area ?? ""}`),
      metaA: f.kpiA ? meta(f.metaA, f.metaTextoA, f.unidadA) : SIN_DATO,
      metaB: f.kpiB ? meta(f.metaB, f.metaTextoB, f.unidadB) : SIN_DATO,
      alcanceA: celdaAlcance(f.kpiA != null, f.alcanceA, f.semaforoA, f.kpiA?.estatus === "RETIRADO",
        c.mismoCorte === true && (c.trimestre ?? 4) < 4 ? f.alcanceCierreA : null),
      alcanceB: celdaAlcance(f.kpiB != null, f.alcanceB, f.semaforoB, f.kpiB?.estatus === "RETIRADO", null),
      delta: d == null ? SIN_DATO : pts(d),
      deltaClase: claseDiferencia(d),
      estado,
      estadoTexto: textoEstado(estado, f, c.anioB),
      valorEstado: f.estado ?? null,
      pesosCambiados: f.pesosCambiados === true,
      cambioMeta: metaCambiada ? f.cambioMeta || "Meta distinta" : "",
      cambioPesos: f.pesosCambiados ? `Pesos: ${f.cambioPesos ?? "distintos"}` : "",
      motivo,
      quien,
      sinCambio: textoSinCambio(f, conCambio),
      idEjercicioKpiA: f.kpiA?.idEjercicioKpi ?? null,
      idEjercicioKpiB: f.kpiB?.idEjercicioKpi ?? null,
      fila: f,
    };
  }

  /** Sin renglón CAMBIO_META: un año importado no lo trae; uno de T3 lo escribe al autorizar. */
  private sinMotivo(c: BscComparativo): string {
    const importado = (o: OrigenPlaneacionBsc | undefined) => o === "IMPORTACION_HISTORICA" || o === "IMPORTACION_ARRANQUE";
    if (c.idBscArea != null && importado(c.origenPlaneacionB)) {
      return `Sin motivo registrado: ${c.anioB} se cargó del FOR-011`;
    }
    return "Sin motivo registrado";
  }

  onFiltro(texto: string): void {
    this.filtro = texto;
    this.aplicaFiltro();
  }

  onFiltroEstado(): void {
    this.aplicaFiltro();
  }

  private aplicaFiltro(): void {
    const buscado = normaliza(this.filtro).trim();
    const lista = this.filas.filter((f) =>
      (!buscado || f.busqueda.includes(buscado)) &&
      (this.filtroEstado === "TODOS" || f.valorEstado === this.filtroEstado ||
        (this.filtroEstado === "CONSERVADO" && f.valorEstado === "META_MODIFICADA")));
    this.filasVisibles = lista;
    // Toda la empresa: un grupo por área con renglones visibles (llegan juntos, en el orden del servidor).
    const porArea = this.comparativo?.idBscArea == null;
    const grupos: GrupoVista[] = [];
    let previa: number | null | undefined;
    for (const f of lista) {
      const ultimo = grupos.at(-1);
      if (ultimo && (!porArea || f.idBscArea === previa)) {
        ultimo.filas.push(f);
      } else {
        grupos.push({ clave: `${f.idBscArea ?? ""}|${grupos.length}`, area: f.area, conEncabezado: porArea, filas: [f] });
      }
      previa = f.idBscArea;
    }
    this.gruposVisibles = grupos;
  }

  /** Conteo por filtro de estado (los conservados incluyen los de meta modificada, como el tile). */
  conteo(valor: FiltroEstado): number {
    if (valor === "TODOS") {
      return this.filas.length;
    }
    return this.filas.filter((f) => f.valorEstado === valor ||
      (valor === "CONSERVADO" && f.valorEstado === "META_MODIFICADA")).length;
  }

  textoFiltro(valor: FiltroEstado): string {
    switch (valor) {
      case "TODOS":
        return "Todos";
      case "CONSERVADO":
        return "Conservados";
      case "META_MODIFICADA":
        return "Con meta modificada";
      case "NUEVO":
        return `Nuevos en ${this.comparativo?.anioB ?? "B"}`;
      default:
        return `Retirados en ${this.comparativo?.anioB ?? "B"}`;
    }
  }

  /** Columnas de la tabla (para la fila de grupo de cada área). */
  get columnas(): number {
    return 9;
  }

  /** La ficha (A3) de un KPI en un año, con el corte del comparativo. */
  queryFicha(anio: "A" | "B"): Record<string, string | number | null> {
    const c = this.comparativo;
    const q = anio === "A" ? c?.trimestreA : c?.trimestreB;
    return { corte: q != null ? `${q}` : "ANUAL", q: q ?? null };
  }

  /* ============================ Exportar ============================ */

  /** La tabla tal como se ve (con sus filtros), con xlsx en el navegador (§10: exportación plana). */
  async exportar(): Promise<void> {
    const c = this.comparativo;
    if (!c || this.exportando || this.filasVisibles.length === 0) {
      return;
    }
    this.exportando = true;
    this.errorExportar = "";
    this.cdr.detectChanges();
    try {
      const XLSX = await import("xlsx");
      const corte = c.mismoCorte ? ` (Q${c.trimestre})` : "";
      const corteA = c.mismoCorte ? corte : ` (Q${c.trimestreA ?? "-"})`;
      const corteB = c.mismoCorte ? corte : ` (Q${c.trimestreB ?? "-"})`;
      const encabezado = [
        "Área", "KPI maestro", "Indicador", `Meta ${c.anioA}`, `Alcance ${c.anioA}${corteA}`,
        `Meta ${c.anioB}`, `Alcance ${c.anioB}${corteB}`, "Diferencia (pts)",
        "Estado", "Cambio de meta", "Cambio de pesos", "Motivo",
      ];
      const renglones = this.filasVisibles.map((v) => [
        v.area,
        v.objetivo,
        v.indicador,
        v.metaA === SIN_DATO ? "" : v.metaA,
        numero(v.fila.alcanceA),
        v.metaB === SIN_DATO ? "" : v.metaB,
        numero(v.fila.alcanceB),
        numero(v.fila.delta) == null ? null : Math.round((numero(v.fila.delta) as number) * 1000) / 10,
        v.estadoTexto,
        v.fila.cambioMeta ?? "",
        v.fila.cambioPesos ?? "",
        v.fila.motivo ?? "",
      ]);
      const hoja = XLSX.utils.aoa_to_sheet([encabezado, ...renglones]);
      // Los alcances como porcentaje de Excel (la API manda fracciones).
      for (let r = 1; r <= renglones.length; r++) {
        for (const col of [4, 6]) {
          const celda = hoja[XLSX.utils.encode_cell({ r, c: col })];
          if (celda?.t === "n") {
            celda.z = "0.0%";
          }
        }
      }
      hoja["!cols"] = [{ wch: 18 }, { wch: 60 }, { wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        { wch: 12 }, { wch: 18 }, { wch: 28 }, { wch: 44 }, { wch: 50 }];
      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, "Comparativo");
      const nombre = `Comparativo BSC ${this.nombreArea} ${c.anioA} vs ${c.anioB}`.replace(/[\\/?*[\]:]/g, "-");
      XLSX.writeFile(libro, `${nombre}.xlsx`);
    } catch {
      this.errorExportar = "No se pudo generar el archivo de Excel en el navegador.";
    }
    this.exportando = false;
    this.cdr.detectChanges();
  }

  /* ============================ Apoyo ============================ */

  trackFila(_: number, f: FilaVista): string {
    return f.clave;
  }

  trackGrupo(_: number, g: GrupoVista): string {
    return g.clave;
  }

  trackArea(_: number, a: FilaArea): string {
    return `${a.idBscArea ?? a.area}`;
  }
}

/* ============================ Funciones de la vista ============================ */

function anioValido(valor: string | null): number | null {
  const n = Number(valor);
  return Number.isInteger(n) && n > 1900 && n < 3000 ? n : null;
}

function idValido(valor: string | null): number | null {
  const n = Number(valor);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function textoAnio(anio: number, ciclo: BscCiclo | undefined): string {
  if (!ciclo) {
    return `${anio} · sin configurar`;
  }
  if (ciclo.estatus === "CERRADO") {
    return `${anio} · cerrado`;
  }
  if (ciclo.origen === "IMPORTACION_HISTORICA") {
    return `${anio} · histórico`;
  }
  return `${anio} · abierto`;
}

function textoTrimestre(q: number | null | undefined): string {
  return q ? `Q${q}` : "sin trimestre autorizado";
}

/** La celda de alcance de un KPI en un año. */
function celdaAlcance(
  esta: boolean,
  valor: number | null | undefined,
  semaforo: SemaforoBsc | null | undefined,
  retirado: boolean,
  cierre: number | null | undefined,
): CeldaAlcance {
  if (!esta) {
    return { texto: SIN_DATO, clase: "is-vacio", nota: "" };
  }
  if (retirado) {
    return { texto: "Retirado", clase: "is-vacio", nota: "" };
  }
  if (semaforo?.startsWith("INICIA_Q")) {
    return { texto: `Inicia en Q${semaforo.slice(-1)}`, clase: "is-vacio", nota: "" };
  }
  const n = numero(valor);
  const c = numero(cierre);
  return {
    texto: n == null ? SIN_DATO : pct(n),
    clase: n == null ? "is-vacio" : "",
    nota: c != null ? `cierre ${pct(c)}` : "",
  };
}

function filaArea(a: BscComparativoArea): FilaArea {
  const d = numero(a.delta);
  const sinPlaneacion = (e: EstatusPlaneacionBsc | undefined) => e == null;
  let nota = "";
  if (sinPlaneacion(a.estatusPlaneacionA)) {
    nota = "Sin planeación el año A";
  } else if (sinPlaneacion(a.estatusPlaneacionB)) {
    nota = "Sin planeación el año B";
  } else if (a.estatusPlaneacionB !== "AUTORIZADA") {
    const e = ESTATUS_PLANEACION.find((x) => x.valor === a.estatusPlaneacionB);
    nota = `Planeación B ${e ? e.texto.toLowerCase() : "sin autorizar"}`;
  }
  return {
    idBscArea: a.idBscArea ?? null,
    area: a.area || SIN_DATO,
    alcanceA: pct(a.alcanceA),
    alcanceB: pct(a.alcanceB),
    delta: d == null ? SIN_DATO : pts(d),
    deltaClase: claseDiferencia(d),
    nota,
  };
}

function ficha(datos: DatosAnio): FichaAnio {
  const { anio, rol, estatus, origen, q, objetivo, porOmision, datosHasta, mismoCorte } = datos;
  const e = ESTATUS_PLANEACION.find((x) => x.valor === estatus);
  const ultimo = mismoCorte ? "" : " (último autorizado)";
  return {
    anio: anio ?? 0,
    rol,
    planeacion: e ? e.texto : "Sin planeación",
    origen: textoOrigen(origen),
    trimestre: q ? `Q${q}${ultimo}` : "Sin trimestre autorizado",
    objetivo: pct(objetivo),
    porOmision: porOmision === true,
    datos: datosHasta ? `El FOR-011 se cargó hasta Q${datosHasta}: después no hay datos` : "",
  };
}

/** De dónde salió la planeación de un año. */
function textoOrigen(origen: OrigenPlaneacionBsc | undefined): string {
  switch (origen) {
    case "IMPORTACION_HISTORICA":
      return "Histórico importado del FOR-011";
    case "IMPORTACION_ARRANQUE":
      return "Arranque importado del FOR-011";
    case "PLANEACION":
      return "Planeación de metas";
    default:
      return SIN_DATO;
  }
}

/** "2026 contra 44% en 2025" (mismo corte) o "2026 (Q2) contra 44% en 2025 (Q4)" (cierre anual). */
function metaAlcance(c: BscComparativo, a: number | null): string {
  if (c.mismoCorte) {
    const contra = a != null ? `${pct(a)} en ${c.anioA}` : `${c.anioA} sin dato a ese corte`;
    return `${c.anioB} contra ${contra}`;
  }
  const alcanceA = a != null ? pct(a) : SIN_DATO;
  return `${c.anioB} (${textoTrimestre(c.trimestreB)}) contra ${alcanceA} en ${c.anioA} (${textoTrimestre(c.trimestreA)})`;
}

/** El tono del tile por la diferencia: apagado sin dato, en meta arriba o igual, abajo en el tono del total. */
function claseAlcance(d: number | null): string {
  if (d == null) {
    return "ticket-kpi--muted";
  }
  return d >= 0 ? "ticket-kpi--done" : "ticket-kpi--total";
}

/** "arriba de", "abajo de" o "igual que", según el signo de la diferencia. */
function sentidoDelta(d: number): string {
  if (d > 0) {
    return "arriba de";
  }
  return d < 0 ? "abajo de" : "igual que";
}

/** El tono de la nota del tile: verde arriba, rojo abajo, sin tono si es igual. */
function notaClaseDelta(d: number): TileBsc["notaClase"] {
  if (d > 0) {
    return "is-exito";
  }
  return d < 0 ? "is-peligro" : undefined;
}

/** El tono de una diferencia en las tablas: is-arriba, is-abajo o nada en cero o sin dato. */
function claseDiferencia(d: number | null): FilaVista["deltaClase"] {
  if (d == null || d === 0) {
    return "";
  }
  return d > 0 ? "is-arriba" : "is-abajo";
}

/** El estado en palabras: «Nuevo en 2026», «Retirado en 2026» o el texto del estado. */
function textoEstado(
  estado: EstadoBsc<EstadoComparativoBsc> | null,
  f: BscComparativoFila,
  anioB: number | undefined,
): string {
  if (estado == null) {
    return SIN_DATO;
  }
  if (f.estado === "NUEVO") {
    return `Nuevo en ${anioB}`;
  }
  return f.estado === "RETIRADO" ? `Retirado en ${anioB}` : estado.texto;
}

/** «Sin cambio» en un conservado sin pesos distintos; vacío si cambió; «—» en lo demás. */
function textoSinCambio(f: BscComparativoFila, conCambio: boolean): string {
  if (f.estado === "CONSERVADO" && !f.pesosCambiados) {
    return "Sin cambio";
  }
  return conCambio ? "" : SIN_DATO;
}

/** Quién y cuándo cambió la meta, y en qué planeación. */
function quienCambio(f: BscComparativoFila): string {
  return [f.usuarioCambio, f.fechaCambio ? fechaCorta(f.fechaCambio) : null, f.anioCambio ? `planeación ${f.anioCambio}` : null]
    .filter((x) => !!x).join(" · ");
}

/** Sin acentos y en minúsculas, para buscar. */
function normaliza(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}
